import React from 'react';
import { BadgeCheckIcon, CarIcon, StarIcon } from 'lucide-react';
import { FitBadge } from './ui/FitBadge';
import { Tag } from './ui/Tag';
import { SongRow } from './SongRow';
import { weekDays } from '../data/options';
import { baht } from '../utils/format';
import type { Fit } from '../utils/fit';
import type { Performer } from '../types/marketplace';
interface PerformerDetailProps {
  performer: Performer;
  fit?: Fit;
  travel: number;
  actions?: React.ReactNode;
}
export function PerformerDetail({
  performer,
  fit,
  travel,
  actions
}: PerformerDetailProps) {
  return <div className="pb-2" data-id="element-630" data-mp="1dhavt8s52s0">
      <div className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5" data-id="element-631" data-mp="1e99lcxobgmi">
        {performer.photos.map((p, i) => <img key={p + i} src={p} alt={i === 0 ? performer.name : ''} className="aspect-[4/5] w-[70%] shrink-0 snap-center rounded-md object-cover" data-id="element-632" data-mp="5gvu0ss0lz9o" data-mpi={i} />)}
      </div>

      <div className="mt-5 flex items-start justify-between gap-3" data-id="element-633" data-mp="1zriwqn5hv9ar">
        <div data-id="element-634" data-mp="2h8tb73l7ewnu">
          <h2 className="flex items-center gap-2 text-[26px] font-bold leading-tight tracking-[-0.015em]" data-id="element-635" data-mp="2h89qjf465u64">
            {performer.name}
            {performer.verified && <BadgeCheckIcon className="h-6 w-6 text-brand-strong" aria-label="Verified" data-id="element-636" data-mp="2qz4mjb77w84r" />}
          </h2>
          <p className="mt-1 flex items-center gap-1 text-[15px] text-ink-2" data-id="element-637" data-mp="2m0baln7kwp6p">
            {performer.actType}
            {performer.members > 1 ? ` · ${performer.members}-piece` : ''} · {performer.city}
          </p>
        </div>
        {fit && <FitBadge score={fit.score} onPhoto={false} data-id="element-638" data-mp="1tcnsqfxp0cnr" />}
      </div>
      {fit && <p className="mt-3 flex items-center gap-2 text-sm text-ink-2" data-id="element-639" data-mp="288woh7idk7vf">
          <span className="h-1.5 w-1.5 rounded-full bg-brand" aria-hidden="true" data-id="element-640" data-mp="239tjfn2ea1ng" />
          {fit.reason}
        </p>}

      <dl className="mt-6 grid grid-cols-3 border-y border-line py-4 text-center" data-id="element-641" data-mp="3jn0igdoypw0h">
        <div data-id="element-642" data-mp="3ep8eumeyglgg">
          <dt className="text-[13px] font-semibold text-ink-3" data-id="element-643" data-mp="3epiaapirrktj">Fee</dt>
          <dd className="tabular mt-0.5 text-lg font-bold" data-id="element-644" data-mp="3ls5jclycgkzh">{baht(performer.price)}</dd>
        </div>
        <div className="border-x border-line" data-id="element-645" data-mp="3qqq8nqc25k95">
          <dt className="text-[13px] font-semibold text-ink-3" data-id="element-646" data-mp="3qpf4vqim4ci1">Rating</dt>
          <dd className="tabular mt-0.5 flex items-center justify-center gap-1 text-lg font-bold" data-id="element-647" data-mp="3vptg1hfig2ka">
            <StarIcon className="h-4 w-4 fill-current text-brand-strong" aria-hidden="true" data-id="element-648" data-mp="4k1runlan004" />
            {performer.rating.toFixed(1)}
            <span className="text-sm font-semibold text-ink-3" data-id="element-649" data-mp="3338oogz1bqgq">({performer.reviewCount})</span>
          </dd>
        </div>
        <div data-id="element-650" data-mp="37y5s98djp7m8">
          <dt className="text-[13px] font-semibold text-ink-3" data-id="element-651" data-mp="3czv8r3jjedhp">Gigs played</dt>
          <dd className="tabular mt-0.5 text-lg font-bold" data-id="element-652" data-mp="3cxqupsrxby3p">{performer.gigsPlayed}</dd>
        </div>
      </dl>

      <div className="mt-4 flex items-center gap-3 rounded-md bg-surface-2 px-4 py-3.5" data-id="element-653" data-mp="2kaxqv1j8miph">
        <CarIcon className="h-5 w-5 shrink-0 text-ink-2" data-id="element-654" data-mp="2k93e4ai4dwwh" />
        <p className="text-sm text-ink-2" data-id="element-655" data-mp="2p8prcjxy7d5a">
          Travel to your venue ≈ <span className="tabular font-bold text-ink" data-id="element-656" data-mp="16fejsfd0ehq5">{baht(travel)}</span> · {performer.distanceKm} km away, travels up to {performer.travelRadiusKm} km
        </p>
      </div>

      <p className="mt-5 text-[16px] leading-relaxed text-ink-2" data-id="element-657" data-mp="xe7yzu3bukr3">{performer.bio}</p>
      <div className="mt-4 flex flex-wrap gap-1.5" data-id="element-658" data-mp="xgax71emvk6w">
        {performer.genres.map(g => <Tag key={g} data-id="element-659" data-mp="sh7sji2kffzg">{g}</Tag>)}
      </div>

      <h3 className="mt-7 text-lg font-bold" data-id="element-660" data-mp="ngaar6lgwnyh">Keys</h3>
      <div className="mt-3 space-y-3" data-id="element-661" data-mp="nhv6v0nfkeyg">
        {performer.keys.map(k => <SongRow key={k.id} song={k} data-id="element-662" data-mp="io0qa634gg3l" />)}
      </div>

      <h3 className="mt-7 text-lg font-bold" data-id="element-663" data-mp="emxcdp4mwu50">Usually available</h3>
      <div className="mt-3 flex gap-1.5" data-id="element-664" data-mp="9om1rc6imflq">
        {weekDays.map(d => {
        const on = performer.availability.includes(d);
        return <span key={d} className={`grid h-10 flex-1 place-items-center rounded-sm text-[13px] font-bold ${on ? 'bg-ink text-bg' : 'bg-surface-2 text-ink-3'}`} data-id="element-665" data-mp="4o7tpu4wz5np">
              {d.slice(0, 2)}
            </span>;
      })}
      </div>

      <h3 className="mt-7 text-lg font-bold" data-id="element-666" data-mp="24qquv8omrldx">Reviews</h3>
      <ul className="mt-2 divide-y divide-line" data-id="element-667" data-mp="1q2w0lnuz58yq">
        {performer.reviews.map(r => <li key={r.author} className="py-3" data-id="element-668" data-mp="1q11nuwtuwn5q">
            <p className="text-[15px] leading-relaxed" data-id="element-669" data-mp="1v0xjo8seh8a7">“{r.text}”</p>
            <p className="mt-1 text-sm font-semibold text-ink-3" data-id="element-670" data-mp="1h2hb0caz97pf">{r.author}</p>
          </li>)}
      </ul>

      {actions && <div className="mt-6" data-id="element-671" data-mp="1m2c32j6ho3c2">{actions}</div>}
    </div>;
}